/** The shell: masthead, global search, nav and the route table. */

import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { Routes, navigate, useLocation, type Route } from './lib/router';
import { trackPageView } from './lib/analytics';
import {
  CompanyList, MunicipalityList, PersonList, SubsidyList, TenderList,
} from './components/lists';
import { PageHead, ErrorState, Loading } from './components/ui';
import { Logo } from './components/Logo';
import { MunicipalityPage } from './pages/Municipality';
import { CompanyPage } from './pages/Company';
import { PersonPage, PartyPage, TenderPage } from './pages/Dossiers';
import { SubsidyPage } from './pages/Subsidy';
import { OverviewPage } from './pages/Overview';
import { SignalsPage } from './pages/Signals';
import { ObservationsPage } from './pages/Observations';
import { PartiesPage } from './pages/Parties';
import { NetworkPage } from './pages/Network';
import { ActivityPage } from './pages/Activity';
/* Cytoscape is most of the bundle and most visits never open a graph, so
   the view is fetched on demand. */
const GraphPage = lazy(async () => ({ default: (await import('./pages/Graph')).GraphPage }));
/* The operator's screen. Lazy for a different reason than the graph: it
   is not part of any visitor's session, so it does not belong in the
   bundle they download. */
const AdminPage = lazy(async () => ({ default: (await import('./pages/Admin')).AdminPage }));
import { SearchPage } from './pages/Search';
import { PrivacyPage } from './pages/Privacy';
import { MinistriesPage } from './pages/Ministries';
import { MinistryPage } from './pages/Ministry';
import { CookieGate } from './components/CookieGate';

/* Grouped rather than flat: eleven top-level links had outgrown the bar, and
   the groupings say something — registers describe who exists, the money
   section what was paid, and the analysis section is where this project's
   own judgement enters. A reader deserves to see that boundary. */
const NAV: { label: string; href?: string; items?: { href: string; segment: string; label: string }[] }[] = [
  { label: 'Обзор', href: '#/' },
  {
    label: 'Регистри',
    items: [
      { href: '#/companies', segment: 'companies', label: 'Фирми' },
      { href: '#/people', segment: 'people', label: 'Лица' },
      { href: '#/municipalities', segment: 'municipalities', label: 'Общини' },
      { href: '#/ministries', segment: 'ministries', label: 'Министерства' },
      { href: '#/parties', segment: 'parties', label: 'Партии' },
    ],
  },
  {
    label: 'Пари',
    items: [
      { href: '#/tenders', segment: 'tenders', label: 'Поръчки' },
      { href: '#/subsidies', segment: 'subsidies', label: 'Субсидии' },
    ],
  },
  {
    label: 'Анализ',
    items: [
      { href: '#/signals', segment: 'signals', label: 'Съвпадения за проверка' },
      { href: '#/network', segment: 'network', label: 'Мрежа на съвместно участие' },
      { href: '#/activity', segment: 'activity', label: 'Карта на дейността' },
      { href: '#/graph', segment: 'graph', label: 'Граф на свързаност' },
    ],
  },
  {
    label: 'Данни',
    items: [
      { href: '#/observations', segment: 'observations', label: 'Наблюдения' },
    ],
  },
];

function listPage(eyebrow: string, title: string, lede: string, list: ReactNode) {
  return (
    <>
      <PageHead eyebrow={eyebrow} title={title} lede={lede} />
      {list}
    </>
  );
}

const ROUTES: Route[] = [
  { pattern: '/', render: () => <OverviewPage /> },
  {
    pattern: '/companies',
    render: () => listPage('Регистър', 'Фирми',
      'Търговски дружества, обединения и консорциуми, срещани в поръчки, субсидии и регистъра.',
      <CompanyList />),
  },
  {
    pattern: '/people',
    render: () => listPage('Регистър', 'Лица',
      'Собственици, управители и избрани кметове. Идентификаторите са псевдонимизирани — '
      + 'имената идват от публичните регистри.',
      <PersonList />),
  },
  {
    pattern: '/municipalities',
    render: () => listPage('Територия', 'Общини',
      'Население по данни на ГРАО, избран кмет и издигаща партия по данни на ЦИК, '
      + 'и възлаганите поръчки — общо и на жител. Таблицата се отваря подредена по '
      + '„На жител“: къде минават най-много пари спрямо броя на живеещите.',
      <MunicipalityList />),
  },
  {
    pattern: '/tenders',
    render: () => listPage('Обществени поръчки', 'Поръчки',
      'Възложени договори и обявления от ЦАИС ЕОП и TED. Броевете до всеки филтър показват '
      + 'какво ще остане, ако го изберете.',
      <TenderList />),
  },
  {
    pattern: '/subsidies',
    render: () => listPage('Европейски средства', 'Субсидии',
      'Изплатени средства по ИСУН и ДФ „Земеделие“. Плащания към физически лица не се зареждат.',
      <SubsidyList />),
  },
  { pattern: '/companies/:uic', render: (params) => <CompanyPage uic={params['uic']!} /> },
  { pattern: '/municipalities/:key', render: (params) => <MunicipalityPage municipalityKey={params['key']!} /> },
  { pattern: '/people/:hash', render: (params) => <PersonPage hash={params['hash']!} /> },
  { pattern: '/ministries', render: () => <MinistriesPage /> },
  { pattern: '/ministries/:uic', render: (params) => <MinistryPage uic={params['uic']!} /> },
  { pattern: '/parties', render: () => <PartiesPage /> },
  { pattern: '/parties/:key', render: (params) => <PartyPage partyKey={params['key']!} /> },
  { pattern: '/tenders/:id', render: (params) => <TenderPage id={params['id']!} /> },
  { pattern: '/subsidies/:id', render: (params) => <SubsidyPage id={decodeURIComponent(params['id']!)} /> },
    { pattern: '/signals', render: () => <SignalsPage /> },
  { pattern: '/network', render: () => <NetworkPage /> },
  { pattern: '/activity', render: () => <ActivityPage /> },
  { pattern: '/observations', render: () => <ObservationsPage /> },
  {
    pattern: '/graph',
    render: () => (
      <Suspense fallback={<Loading label="Зареждане на графа…" variant="text" rows={4} />}>
        <GraphPage />
      </Suspense>
    ),
  },
  { pattern: '/search', render: () => <SearchPage /> },
  /* Not in the nav: reached from the footer and the consent banner. */
  { pattern: '/privacy', render: () => <PrivacyPage /> },
  /* Unlisted: the nav is for readers, this is for the operator. */
  {
    pattern: '/admin',
    render: () => (
      <Suspense fallback={<Loading />}>
        <AdminPage />
      </Suspense>
    ),
  },
];

function Masthead() {
  const { location } = useLocation();
  const active = location.path.split('/')[1] ?? '';
  const [term, setTerm] = useState('');
  /* The phone drawer. On wide screens the same <nav> lays out as the bar
     with hover menus and this state is simply never true. */
  const [menuOpen, setMenuOpen] = useState(false);

  // a navigation closes the drawer; so does Escape
  useEffect(() => { setMenuOpen(false); }, [location.path]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <>
      <header className="masthead">
        <div className="masthead-top">
          <a className="wordmark" href="#/">
            <Logo />
            <span className="wordmark-text">
              <span className="wordmark-title">Алфа<span className="wordmark-dash">–</span>Омега</span>
              <span className="wordmark-sub">От бюджета до получателя</span>
            </span>
          </a>
          <form
            className="global-search"
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              const query = term.trim();
              if (query) navigate(`/search?q=${encodeURIComponent(query)}`);
            }}
          >
            <label className="sr-only" htmlFor="global-q">Търсене по име или ЕИК</label>
            <input
              type="search"
              id="global-q"
              placeholder="Име или ЕИК…"
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
            <button type="submit" aria-label="Търси">↵</button>
          </form>
          <button
            type="button"
            className="nav-burger"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            aria-label={menuOpen ? 'Затвори менюто' : 'Отвори менюто'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span /><span /><span />
          </button>
        </div>
        <nav id="site-nav" className={menuOpen ? 'nav is-open' : 'nav'}>
          {/* re-selecting the current page changes no location, so every
              link also closes the drawer on the click itself */}
          {NAV.map((group) => {
            if (group.href) {
              return (
                <a
                  key={group.label}
                  href={group.href}
                  aria-current={active === '' ? 'page' : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {group.label}
                </a>
              );
            }
            const within = (group.items ?? []).some((item) => item.segment === active);
            return (
              /* Opened on hover and on focus, so it is reachable from the
                 keyboard as well as the pointer. In the drawer the button is
                 inert and reads as the section heading. */
              <div className="nav-group" key={group.label}>
                <button type="button" className={within ? 'nav-top is-active' : 'nav-top'}>
                  {group.label}
                  <span className="nav-caret">▾</span>
                </button>
                <div className="nav-menu">
                  {(group.items ?? []).map((item) => (
                    <a
                      key={item.segment}
                      href={item.href}
                      className={item.segment === active ? 'is-active' : undefined}
                      aria-current={item.segment === active ? 'page' : undefined}
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </header>
      {menuOpen && (
        <div className="nav-scrim" aria-hidden="true" onClick={() => setMenuOpen(false)} />
      )}
    </>
  );
}

export function App() {
  const { location } = useLocation();

  // A new dossier starts at the top; changing a filter on the page you are
  // reading must not yank you back up, so only the path is watched.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.path]);

  // The path only, for the same reason: a filter change rewrites the query
  // string with replaceState and is not a new page. Counting those would
  // report a reader narrowing one list as a dozen visits. No-op unless a
  // measurement ID was configured at build time.
  useEffect(() => {
    trackPageView(location.path);
  }, [location.path]);

  return (
    <>
      <Masthead />
      <CookieGate />
      <main className="view" aria-live="polite">
        <Routes
          routes={ROUTES}
          fallback={<ErrorState message="Страницата не е намерена." />}
        />
      </main>
      <footer className="colophon">
        <span>
          Алфа-Омега · Данни: ЦАИС ЕОП · TED · ИСУН · ДФЗ · Търговски регистър · ЦИК · ГРАО
        </span>
        <span className="colophon-note">
          Сумите са в лева (EUR по фиксирания курс 1,95583).
          Личните идентификатори са псевдонимизирани (SHA-256).
          {' · '}
          <a href="#/privacy">Политика за поверителност</a>
        </span>
      </footer>
    </>
  );
}
